import clsx from 'clsx';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { StripHTML } from '@/utils/utils';
import { HubertButton } from 'components/HubertUI/HubertButton';
import { useGetQuestionByIdQuery, useShareWithAccessGroupsMutation } from 'rtk/query/apis/questions';
import type { TVisibility } from 'types/template/template.types';
import type { TShareWithAccessGroupsResult, TTemplateAudienceProblem } from 'types/template/templateAudience.types';
import { questionsSharingCanFix, withoutProblemsFixedBySharing } from 'utils/templateAudienceSharing';

const MAX_QUESTION_TEXT_LENGTH = 80;

type TShareFailure = {
  questionId: string;
  reason: Extract<TShareWithAccessGroupsResult, { shared: false }>['reason'] | 'unknown';
};

interface CopyAudienceProblemsListProps {
  problems: ReadonlyArray<TTemplateAudienceProblem>;
  renderQuestion: (questionId: string) => ReactNode;
  share?: {
    questionCount: number;
    isSharing: boolean;
    onShare: () => void;
    failures: ReadonlyArray<TShareFailure>;
  };
  copyVisibility?: TVisibility;
}

export const CopyAudienceProblemsList = ({
  problems,
  renderQuestion,
  share,
  copyVisibility,
}: CopyAudienceProblemsListProps) => {
  const { t } = useTranslation('Templates');
  const isOrganizationCopy = copyVisibility === 'client';

  if (share && !problems.length) {
    return (
      <div
        role="status"
        className={clsx('mt-4 w-full', 'rounded-md px-4 py-3', 'bg-success-50 text-success-800', 'text-sm')}
      >
        {t('createTemplate.copyProblems.sharedAll')}
      </div>
    );
  }

  const describe = (problem: TTemplateAudienceProblem) => {
    if (problem.kind === 'question') return t(`createTemplate.copyProblems.${problem.reason}`);
    if (problem.issue === 'target_other_client') {
      return problem.ruleName
        ? t('createTemplate.copyProblems.ruleOtherClient', { ruleName: problem.ruleName })
        : t('createTemplate.copyProblems.ruleUnnamedOtherClient');
    }
    if (isOrganizationCopy) {
      return problem.ruleName
        ? t('createTemplate.copyProblems.ruleOrganization', { ruleName: problem.ruleName })
        : t('createTemplate.copyProblems.ruleUnnamedOrganization');
    }
    return problem.ruleName
      ? t('createTemplate.copyProblems.rule', { ruleName: problem.ruleName })
      : t('createTemplate.copyProblems.ruleUnnamed');
  };

  return (
    <div
      role="alert"
      className={clsx(
        'mt-4 w-full',
        'rounded-md px-4 py-3',
        'bg-danger-50 text-danger-500',
        'text-sm',
        'flex flex-col gap-2',
      )}
    >
      <div className="font-semibold">
        {isOrganizationCopy
          ? t('createTemplate.copyProblems.titleOrganization')
          : t('createTemplate.copyProblems.title')}
      </div>
      <ul className="flex list-disc flex-col gap-1 pl-5">
        {problems.map((problem) => (
          <li
            key={`${problem.kind}-${problem.questionId}-${problem.kind === 'rule' ? problem.ruleId : problem.reason}`}
          >
            <span className="font-medium">{renderQuestion(problem.questionId)}</span> {describe(problem)}
          </li>
        ))}
      </ul>
      <div>
        {isOrganizationCopy ? t('createTemplate.copyProblems.hintOrganization') : t('createTemplate.copyProblems.hint')}
      </div>
      {share && share.questionCount > 0 ? (
        <div className="flex flex-col items-start gap-1">
          <HubertButton size="xs" variant="outline" disabled={share.isSharing} onClick={share.onShare}>
            {t('createTemplate.copyProblems.shareButton', { count: share.questionCount })}
          </HubertButton>
        </div>
      ) : null}
      {share?.failures.map((failure) => (
        <div key={failure.questionId}>
          <span className="font-medium">{renderQuestion(failure.questionId)}</span>:{' '}
          {t(`createTemplate.copyProblems.shareFailed.${failure.reason}`)}
        </div>
      ))}
    </div>
  );
};

const QuestionLabel = ({ questionId }: { questionId: string }) => {
  const { t } = useTranslation('Templates');
  const { data, isError, isLoading } = useGetQuestionByIdQuery({ questionId });

  if (isLoading) return <>…</>;
  if (isError || !data?.questionV4) return <>{t('createTemplate.copyProblems.noAccess')}</>;

  const question = data.questionV4;
  const text = StripHTML(question.text?.[question.available_languages[0]] ?? '')?.trim() || questionId;
  return <>{text.length > MAX_QUESTION_TEXT_LENGTH ? `${text.slice(0, MAX_QUESTION_TEXT_LENGTH - 1)}…` : text}</>;
};

interface CopyAudienceProblemsProps {
  problems: ReadonlyArray<TTemplateAudienceProblem>;
  accessGroupIds: string[];
  onProblemsChange: (problems: TTemplateAudienceProblem[]) => void;
  copyVisibility?: TVisibility;
}

const CopyAudienceProblems = ({
  problems,
  accessGroupIds,
  onProblemsChange,
  copyVisibility,
}: CopyAudienceProblemsProps) => {
  const [shareWithAccessGroups, { isLoading }] = useShareWithAccessGroupsMutation();
  const [failures, setFailures] = useState<TShareFailure[]>([]);

  const failedQuestionIds = failures.map((failure) => failure.questionId);
  const questionIdsToShare = questionsSharingCanFix(problems).filter((id) => !failedQuestionIds.includes(id));

  const onShare = async () => {
    try {
      const { results } = await shareWithAccessGroups({ questionIds: questionIdsToShare, accessGroupIds }).unwrap();
      const sharedQuestionIds = results.filter((result) => result.shared).map((result) => result.questionId);
      setFailures([
        ...failures,
        ...results.flatMap((result) =>
          result.shared ? [] : [{ questionId: result.questionId, reason: result.reason }],
        ),
      ]);
      onProblemsChange(withoutProblemsFixedBySharing(problems, sharedQuestionIds));
    } catch {
      setFailures([
        ...failures,
        ...questionIdsToShare.map((questionId) => ({ questionId, reason: 'unknown' as const })),
      ]);
    }
  };

  return (
    <CopyAudienceProblemsList
      problems={problems}
      renderQuestion={(questionId) => <QuestionLabel questionId={questionId} />}
      copyVisibility={copyVisibility}
      share={
        accessGroupIds.length
          ? { questionCount: questionIdsToShare.length, isSharing: isLoading, onShare, failures }
          : undefined
      }
    />
  );
};

export default CopyAudienceProblems;
